import { Rectangle, Sprite, Texture } from "pixi.js"

import { logger } from "../../../../logger"
import { getRendererApp } from "../renderer_app"

// Alpha at or below this counts as empty. Matches the threshold the death-crumble effect uses for
// the same question ("where does the art actually stop").
const CONTENT_ALPHA_THRESHOLD = 8

// Keyed by the caller's texture id. A composite tile texture is built once and shared by every
// sprite drawn from it, so its content bounds are worth computing once for the whole session --
// the readback is a GPU stall, and a null result (no renderer, e.g. in tests) is cached too so a
// failing lookup isn't retried on every sprite.
// Logical IDs can survive an art change. Texture identity prevents bounds for an older
// generation leaking into its replacement and allows retired textures to be collected.
const cache = new WeakMap<Texture, Map<string, Rectangle | null>>()
const trimmedTextures = new WeakMap<Texture, Texture>()

/** A tight atlas view for carried icons; leaves the shared source texture untouched. */
export const trimTextureToContent = (texture: Texture): Texture => {
    const cached = trimmedTextures.get(texture)
    if (cached) {
        return cached
    }
    // Do not cache a missing renderer: a sprite may be requested before initialization.
    try {
        if (!(getRendererApp().renderer as any)?.plugins?.extract || !texture.valid) {
            return texture
        }
    } catch {
        return texture
    }
    const content = computeContentBounds(texture)
    if (!content) {
        trimmedTextures.set(texture, texture)
        return texture
    }
    const area = texture.trim ?? new Rectangle(0, 0, texture.orig.width, texture.orig.height)
    // Map the crop through the texture UVs, handling trimmed and rotated atlases
    // without copying pixels or uploading a second image to the GPU.
    const uv = texture._uvs
    const corners = [
        [content.x, content.y], [content.right, content.y],
        [content.x, content.bottom], [content.right, content.bottom],
    ].map(([x, y]) => {
        const u = (x - area.x) / area.width
        const v = (y - area.y) / area.height
        return {
            x: (uv.x0 + (uv.x1 - uv.x0) * u + (uv.x3 - uv.x0) * v) * texture.baseTexture.width,
            y: (uv.y0 + (uv.y1 - uv.y0) * u + (uv.y3 - uv.y0) * v) * texture.baseTexture.height,
        }
    })
    const minX = Math.min(...corners.map(p => p.x))
    const minY = Math.min(...corners.map(p => p.y))
    const width = Math.max(...corners.map(p => p.x)) - minX
    const height = Math.max(...corners.map(p => p.y)) - minY
    const trimmed = new Texture(texture.baseTexture, new Rectangle(minX, minY, width, height),
        new Rectangle(0, 0, content.width, content.height), undefined, texture.rotate)
    trimmedTextures.set(texture, trimmed)
    return trimmed
}

// The tight bounds of a texture's non-transparent pixels, in the texture's own pixel space.
//
// This exists because a COMPOSITE tile texture is the bounding box of all the tiles making up an
// object (see TileTextureExtractor), and that box is routinely much wider than the thing you can
// see: the ember campfire's box carries ~32px of empty tile to its left and right. Anything sized
// from the texture rather than its content -- a hover/hit region above all -- is then wrong by that
// much, and the entity reacts to a pointer that is nowhere near it.
export const textureContentBounds = (texture: Texture, cacheKey: string): Rectangle | null => {
    let entries = cache.get(texture)
    if (!entries) { entries = new Map(); cache.set(texture, entries) }
    if (entries.has(cacheKey)) {
        return entries.get(cacheKey)
    }
    const bounds = computeContentBounds(texture)
    entries.set(cacheKey, bounds)
    return bounds
}

const computeContentBounds = (texture: Texture): Rectangle | null => {
    let extract: any
    try {
        extract = (getRendererApp().renderer as any)?.plugins?.extract
    } catch {
        return null
    }
    if (!extract || !texture?.valid) {
        return null
    }
    const probe = new Sprite(texture)
    try {
        const canvas: HTMLCanvasElement = extract.canvas(probe)
        const ctx = canvas.getContext("2d")
        const canvasWidth = canvas.width
        const canvasHeight = canvas.height
        if (!ctx || canvasWidth === 0 || canvasHeight === 0) {
            return null
        }
        const data = ctx.getImageData(0, 0, canvasWidth, canvasHeight).data

        let minX = canvasWidth
        let minY = canvasHeight
        let maxX = -1
        let maxY = -1
        for (let y = 0; y < canvasHeight; y++) {
            const rowOffset = y * canvasWidth * 4
            for (let x = 0; x < canvasWidth; x++) {
                if (data[rowOffset + x * 4 + 3] > CONTENT_ALPHA_THRESHOLD) {
                    if (x < minX) minX = x
                    if (x > maxX) maxX = x
                    if (y < minY) minY = y
                    if (y > maxY) maxY = y
                }
            }
        }
        if (maxX < 0) {
            // Entirely transparent (a blank stand-in marker). No content to point at.
            return null
        }

        // The extracted canvas can be at a different resolution than the texture, so map back through
        // its own dimensions rather than assuming 1:1.
        const area = texture.trim ?? new Rectangle(0, 0, texture.orig.width, texture.orig.height)
        const scaleX = area.width / canvasWidth
        const scaleY = area.height / canvasHeight
        return new Rectangle(
            area.x + minX * scaleX,
            area.y + minY * scaleY,
            (maxX - minX + 1) * scaleX,
            (maxY - minY + 1) * scaleY,
        )
    } catch (e) {
        logger.error({ tags: ["sprite", "error"] }, e)
        return null
    } finally {
        probe.destroy()
    }
}
